import { useEffect, useState, useMemo } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, Mini } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { DollarSign, CreditCard, Receipt, RefreshCcw, AlertTriangle, Archive } from "lucide-react";

const STATUSES = ["all", "paid", "initiated", "refunded", "failed"];

export function Payments() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [lifeTab, setLifeTab] = useState("active");
  const [lifeVersion, setLifeVersion] = useState(0);

  const load = () => api.get("/admin/payments").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const paid = useMemo(() => (items || []).filter((p) => p.payment_status === "paid"), [items]);
  const total = paid.reduce((s, p) => s + Number(p.amount || 0), 0);
  const failed = (items || []).filter((p) => p.payment_status === "failed" || p.payment_status === "expired").length;
  const byKind = useMemo(() => {
    const m = {};
    paid.forEach((p) => { m[p.kind || "other"] = (m[p.kind || "other"] || 0) + Number(p.amount || 0); });
    return m;
  }, [paid]);

  const filtered = useMemo(() => (items || []).filter((p) => {
    if (status !== "all" && p.payment_status !== status) return false;
    if (q && !`${p.description || ""} ${p.email || ""} ${p.kind || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, status, q]);

  const statusColor = (s) => s === "paid" ? "bg-green-500/15 text-green-400 border-green-500/30"
    : s === "refunded" ? "bg-red-500/15 text-red-300 border-red-500/30"
    : (s === "failed" || s === "expired") ? "bg-red-500/15 text-red-300 border-red-500/30"
    : (s === "initiated" || s === "open" || s === "pending") ? "bg-gold/15 text-gold border-gold/30"
    : "bg-white/10 text-gray-400 border-white/20";

  const reconcile = async (p) => {
    setBusyId(p.session_id);
    try { const r = await api.get(`/checkout/status/${p.session_id}`); toast.success(`Reconciled: ${r.data.payment_status || "updated"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };
  const archive = async (p) => {
    if (!window.confirm("Archive this payment? It moves to the Archived tab where you can Restore it or permanently delete it.")) return;
    setBusyId(p.session_id);
    try { await api.post(`/lifecycle/payments/${p.id}/archive`, { reason: "" }); toast.success("Payment archived."); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  if (!items) return <Loader />;
  return (
    <div>
      <Head title="Payments" desc="Every Stripe checkout session, its status, and reconciliation." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Mini label="Total collected" value={money(total)} icon={DollarSign} />
        <Mini label="Paid sessions" value={paid.length} icon={CreditCard} />
        <Mini label="All sessions" value={items.length} icon={Receipt} />
        <Mini label="Failed / expired" value={failed} icon={AlertTriangle} />
      </div>

      {Object.keys(byKind).length > 0 && (
        <div className="bg-navy-800 border border-white/5 p-4 mb-6" data-testid="payments-by-kind">
          <div className="text-gray-400 text-xs uppercase tracking-wide mb-2">Revenue by type</div>
          <div className="flex flex-wrap gap-4">
            {Object.entries(byKind).map(([k, v]) => (
              <div key={k} className="text-sm"><span className="text-gray-500 capitalize">{k}: </span><span className="text-gold font-heading">{money(v)}</span></div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-5"><LifecycleTabs module="payments" value={lifeTab} onChange={setLifeTab} refreshKey={(items || []).length} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="payments" status={lifeTab} singular="Payment" allowDelete={true} onChanged={load} /> : (<>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1 bg-navy-800 border border-white/10 p-1" data-testid="payment-status-filter">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`payment-filter-${s}`}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${status === s ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
              {s}{s === "all" ? ` (${items.length})` : ` (${items.filter((p) => p.payment_status === s).length})`}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search payments…" data-testid="payment-search"
          className="bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none flex-1 min-w-[180px]" />
      </div>

      {filtered.length === 0 ? <EmptyBox text="No payment sessions match your filters." /> : (
        <div className="bg-navy-800 border border-white/5 divide-y divide-white/5" data-testid="payments-list">
          {filtered.map((p) => (
            <div key={p.session_id} className="p-5 flex items-center justify-between gap-4" data-testid={`payment-${p.session_id}`}>
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{p.description || p.kind}</div>
                <div className="text-gray-500 text-xs truncate">{p.email || "guest"} · {p.created_at ? new Date(p.created_at).toLocaleString() : ""}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {(p.payment_status !== "paid" && p.payment_status !== "refunded") && (
                  <button onClick={() => reconcile(p)} disabled={busyId === p.session_id} data-testid={`payment-reconcile-${p.session_id}`} title="Re-check status with Stripe"
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gold disabled:opacity-40"><RefreshCcw size={13} className={busyId === p.session_id ? "animate-spin" : ""} /> Reconcile</button>
                )}
                <span className="font-heading text-lg text-white">{money(p.amount)}</span>
                <span className={`text-xs px-2.5 py-0.5 border capitalize ${statusColor(p.payment_status)}`}>{p.payment_status}</span>
                <button onClick={() => archive(p)} disabled={busyId === p.session_id} data-testid={`payment-archive-${p.session_id}`} title="Archive (recoverable)" className="flex items-center gap-1 text-xs text-gray-400 hover:text-white disabled:opacity-40"><Archive size={13} /> Archive</button>
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
